import type { ProductRecord } from './productRecords';

export type GalleryMode = 'media' | 'products';
export type ProductGallerySource = 'all' | 'featured' | 'collection' | 'category' | 'manual';
export type ProductGallerySort = 'sortOrder' | 'name' | 'updated' | 'featured';
export type ProductGalleryStatus = 'active' | 'all';

export type GalleryItem = {
  id: string;
  src: string;
  alt: string;
  assetId?: string | null;
  link?: string;
};

export type GalleryContentRecord = {
  galleryMode: GalleryMode;
  items: GalleryItem[];
  spacing: number;
  loop: boolean;
  layoutVariant: string;
  columns: number;
  showCaptions: boolean;
  productSource: ProductGallerySource;
  productStatus: ProductGalleryStatus;
  productSort: ProductGallerySort;
  productLimit: number;
  manualProductIds: number[];
  collectionId: string;
  categoryId: string;
  showTitle: boolean;
  showDescription: boolean;
  showPrice: boolean;
  showSelectButton: boolean;
  showView3DButton: boolean;
};

export type ProductGalleryOption = {
  id: string;
  label: string;
};

export const DEFAULT_GALLERY_CONTENT: GalleryContentRecord = {
  galleryMode: 'media',
  items: [],
  spacing: 8,
  loop: false,
  layoutVariant: 'uniform-grid',
  columns: 3,
  showCaptions: false,
  productSource: 'all',
  productStatus: 'active',
  productSort: 'sortOrder',
  productLimit: 12,
  manualProductIds: [],
  collectionId: '',
  categoryId: '',
  showTitle: true,
  showDescription: false,
  showPrice: false,
  showSelectButton: true,
  showView3DButton: true,
};

function normalizeGalleryItems(value: unknown): GalleryItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
      const record = item as Record<string, unknown>;
      const src = typeof record.src === 'string' ? record.src.trim() : '';
      return {
        id:
          typeof record.id === 'string' && record.id.trim().length > 0
            ? record.id.trim()
            : `gallery-item-${index + 1}`,
        src,
        alt:
          typeof record.alt === 'string' && record.alt.trim().length > 0
            ? record.alt.trim()
            : `Gallery image ${index + 1}`,
        assetId:
          typeof record.assetId === 'string' && record.assetId.trim().length > 0
            ? record.assetId.trim()
            : null,
        link:
          typeof record.link === 'string' && record.link.trim().length > 0
            ? record.link.trim()
            : '',
      };
    })
    .filter((item): item is GalleryItem => Boolean(item));
}

function asClampedNumber(value: unknown, fallback: number, min: number, max: number) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, Math.round(parsed)));
}

function asPositiveIdList(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => Number(entry))
    .filter((entry) => Number.isFinite(entry) && entry > 0);
}

export function normalizeGalleryBlockContent(content: unknown): GalleryContentRecord {
  const source =
    content && typeof content === 'object' && !Array.isArray(content)
      ? (content as Record<string, unknown>)
      : {};

  const galleryMode = source.galleryMode === 'products' ? 'products' : 'media';
  const productSource: ProductGallerySource =
    source.productSource === 'featured' ||
    source.productSource === 'collection' ||
    source.productSource === 'category' ||
    source.productSource === 'manual'
      ? source.productSource
      : 'all';
  const productStatus: ProductGalleryStatus =
    source.productStatus === 'all' ? 'all' : 'active';
  const productSort: ProductGallerySort =
    source.productSort === 'name' ||
    source.productSort === 'updated' ||
    source.productSort === 'featured'
      ? source.productSort
      : 'sortOrder';

  return {
    ...DEFAULT_GALLERY_CONTENT,
    galleryMode,
    items: normalizeGalleryItems(source.items),
    spacing: asClampedNumber(source.spacing, DEFAULT_GALLERY_CONTENT.spacing, 0, 64),
    loop: source.loop === true,
    layoutVariant:
      typeof source.layoutVariant === 'string' && source.layoutVariant.trim().length > 0
        ? source.layoutVariant.trim()
        : DEFAULT_GALLERY_CONTENT.layoutVariant,
    columns: asClampedNumber(source.columns, DEFAULT_GALLERY_CONTENT.columns, 1, 4),
    showCaptions: source.showCaptions === true,
    productSource,
    productStatus,
    productSort,
    productLimit: asClampedNumber(source.productLimit, DEFAULT_GALLERY_CONTENT.productLimit, 1, 48),
    manualProductIds: asPositiveIdList(source.manualProductIds),
    collectionId:
      typeof source.collectionId === 'string' && source.collectionId.trim().length > 0
        ? source.collectionId.trim()
        : '',
    categoryId:
      typeof source.categoryId === 'string' && source.categoryId.trim().length > 0
        ? source.categoryId.trim()
        : '',
    showTitle: source.showTitle !== false,
    showDescription: source.showDescription === true,
    showPrice: source.showPrice === true,
    showSelectButton: source.showSelectButton !== false,
    showView3DButton: source.showView3DButton !== false,
  };
}

export function isProductGalleryContent(content: unknown) {
  return normalizeGalleryBlockContent(content).galleryMode === 'products';
}

function sortProducts(products: ProductRecord[], sortMode: ProductGallerySort) {
  const next = [...products];
  if (sortMode === 'updated') {
    return next.sort((left, right) => (right.updatedAt ?? '').localeCompare(left.updatedAt ?? ''));
  }
  if (sortMode === 'name') {
    return next.sort((left, right) => left.name.localeCompare(right.name));
  }
  if (sortMode === 'featured') {
    return next.sort((left, right) => {
      if (left.featured !== right.featured) return left.featured ? -1 : 1;
      return (left.sortOrder ?? 0) - (right.sortOrder ?? 0) || left.name.localeCompare(right.name);
    });
  }
  return next.sort(
    (left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0) || left.name.localeCompare(right.name)
  );
}

export function resolveProductGalleryProducts(
  products: ProductRecord[] | null | undefined,
  content: unknown
) {
  const normalized = normalizeGalleryBlockContent(content);
  if (!Array.isArray(products) || normalized.galleryMode !== 'products') return [] as ProductRecord[];

  const baseProducts =
    normalized.productStatus === 'all'
      ? products
      : products.filter((product) => product.status === 'active');

  let filtered: ProductRecord[] = [];

  if (normalized.productSource === 'manual') {
    const productById = new Map(baseProducts.map((product) => [product.id, product]));
    filtered = normalized.manualProductIds
      .map((productId) => productById.get(productId) ?? null)
      .filter((product): product is ProductRecord => Boolean(product));
  } else if (normalized.productSource === 'featured') {
    filtered = baseProducts.filter((product) => product.featured === true);
  } else if (normalized.productSource === 'collection') {
    filtered = normalized.collectionId
      ? baseProducts.filter((product) => product.collectionId === normalized.collectionId)
      : [];
  } else if (normalized.productSource === 'category') {
    filtered = normalized.categoryId
      ? baseProducts.filter((product) => product.categoryId === normalized.categoryId)
      : [];
  } else {
    filtered = [...baseProducts];
  }

  const ordered =
    normalized.productSource === 'manual'
      ? filtered
      : sortProducts(filtered, normalized.productSort);

  return ordered.slice(0, normalized.productLimit);
}

export function deriveProductGalleryCollections(products: ProductRecord[] | null | undefined): ProductGalleryOption[] {
  if (!Array.isArray(products)) return [];
  const options = new Map<string, string>();
  products.forEach((product) => {
    if (!product.collectionId) return;
    const label = product.collection?.trim() || product.collectionId;
    options.set(product.collectionId, label);
  });
  return Array.from(options.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function deriveProductGalleryCategories(products: ProductRecord[] | null | undefined): ProductGalleryOption[] {
  if (!Array.isArray(products)) return [];
  const options = new Map<string, string>();
  products.forEach((product) => {
    if (!product.categoryId) return;
    const label = product.category?.trim() || product.categoryId;
    options.set(product.categoryId, label);
  });
  return Array.from(options.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
}
